import { useState } from 'react';
import { Button, Popover, Skeleton } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { formatDayLong } from '@/lib/format-utils';
import { Reservation } from '@turizi/shared';
import { useUpdateReservation } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import { IconArrowRight, IconCalendar, IconCalendarCheck, IconCalendarMonth, IconCalendarPlus, IconCalendarWeek } from '@tabler/icons-react';

interface Props {
    reservation: Reservation;
    isLoading: boolean;
    disabled?: boolean;
    className?: string;
    showLabels?: boolean;
}

export default function ReservationDatePicker({ reservation, isLoading, disabled = false, className, showLabels = true }: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [tempDates, setTempDates] = useState<[string | null, string | null]>([null, null]);

    const updateReservation = useUpdateReservation();

    const handleOpen = () => {
        setTempDates([reservation?.start_day, reservation?.end_day]);
        setOpened(true);
    };

    const handleCancel = () => {
        setTempDates([reservation?.start_day, reservation?.end_day]);
        setOpened(false);
    };

    const handleAccept = () => {
        if (tempDates[0] || tempDates[1]) {
            updateReservation.mutate({
                id: reservation.id,
                start_day: tempDates[0],
                end_day: tempDates[1]
            }, {
                onSuccess: () => {
                    setOpened(false);
                },
                onError: () => {
                    notify.error('Oops!', 'error_updating_reservation');
                }
            });
        }
    };

    return (
        <Popover
            withArrow
            shadow="xl"
            radius="md"
            onChange={setOpened}
            opened={opened}
            closeOnClickOutside
            closeOnEscape
            clickOutsideEvents={['mousedown', 'touchstart']}
            disabled={disabled}
        >
            <Popover.Target>
                <div onClick={!disabled ? handleOpen : undefined}>
                    <Skeleton visible={isLoading}>
                        <div
                            className={`
                                ${className} 
                                flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-3 max-w-full
                                ${disabled ? 'solid-box-border-tinted' : 'solid-box-border-hover'}
                            `}
                        >
                            {
                                disabled
                                    ? <IconCalendarCheck size={16} color='gray' stroke={1} className="shrink-0" />
                                    : <IconCalendarWeek size={16} color='gray' stroke={1} className="shrink-0" />
                            }
                            <div className='min-w-0'>
                                {showLabels && <div className='text-gray-400 font-bold mb-2'>{t('arrival')}</div>}
                                <div className='font-semibold leading-tight'>{formatDayLong(reservation?.start_day, '-- / --- / ----')}</div>
                            </div>
                            <div className='text-gray-300 shrink-0'>
                                <IconArrowRight size={12} stroke={3} />
                            </div>
                            <div className='min-w-0'>
                                {showLabels && <div className='text-gray-400 font-bold mb-2'>{t('departure')}</div>}
                                <div className='font-semibold leading-tight'>{formatDayLong(reservation?.end_day, '-- / --- / ----')}</div>
                            </div>
                        </div>
                    </Skeleton>
                </div>
            </Popover.Target>
            <Popover.Dropdown>
                <DatePicker
                    size='md'
                    numberOfColumns={2}
                    type="range"
                    value={tempDates}
                    onChange={setTempDates}
                />
                <div className='flex justify-between mt-4'>
                    <Button
                        variant='subtle'
                        color='black'
                        size='xs'
                        onClick={handleCancel}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="black"
                        size="xs"
                        onClick={handleAccept}
                        loading={updateReservation.isPending}
                    >
                        {t('accept')}
                    </Button>
                </div>
            </Popover.Dropdown>
        </Popover >
    );
}
